import {controller, target} from '@github/catalyst'

@controller
class ModalDialogElement extends HTMLElement {
  @target dialog: HTMLDialogElement
  // Optional: present only on modals whose body is fetched on first open.
  @target content: HTMLElement

  private loaded = false

  // When set, the modal body is fetched from this URL the first time the
  // dialog opens — a modal backed by a slow API shouldn't cost the page that
  // hosts it anything until someone asks for it.
  get src(): string {
    return this.getAttribute('data-src') || ''
  }

  connectedCallback() {
    this.dialog.addEventListener('click', (event: MouseEvent) => {
      if (event.target === this.dialog) {
        this.dialog.close()
      }
    })
  }

  open() {
    this.dialog.showModal()
    if (this.src && !this.loaded) this.load(this.src)
  }

  close() {
    this.dialog.close()
  }

  // Follows a link inside lazily-loaded content (pagination, filters) by
  // swapping the body in place instead of navigating away from the page.
  navigate(event: Event) {
    const link = (event.target as HTMLElement).closest('a')
    if (!link) return

    event.preventDefault()
    this.load(link.href)
  }

  private async load(url: string) {
    if (!this.content) return

    this.loaded = true
    this.content.setAttribute('aria-busy', 'true')
    try {
      const response = await fetch(url, {headers: {Accept: 'text/html'}})
      if (!response.ok) throw new Error(`${response.status}`)
      this.content.innerHTML = await response.text()
    } catch {
      // A failed load must stay retryable — the next open fetches again.
      this.loaded = false
      this.content.innerHTML =
        '<p class="text-red-400 text-sm py-4">Could not load this. Close the dialog and try again.</p>'
    } finally {
      this.content.removeAttribute('aria-busy')
    }
  }
}
